import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { modifyBalanceAdmin } from "../../actions/adminBilling.action";

const UserBillingBlock = ({ userDetails, registerGuard }) => {
    const dispatch = useDispatch();
    const loading = useSelector((state) => state.adminBilling?.loading);
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const isDirty = useMemo(
        () => !!String(amount || "").trim() || !!String(description || "").trim(),
        [amount, description]
    );

    const reset = useCallback(() => {
        setAmount("");
        setDescription("");
        setError("");
        setMessage("");
        return true;
    }, []);

    const handleModify = useCallback(async () => {
        if (!amount || !description.trim()) {
            setError("Заполните сумму и причину корректировки");
            return false;
        }

        setError("");
        setMessage("");

        const success = await dispatch(
            modifyBalanceAdmin("User", userDetails._id, amount, description.trim())
        );

        if (!success) {
            setError("Не удалось изменить баланс");
            return false;
        }

        setAmount("");
        setDescription("");
        setMessage("Баланс пользователя обновлён");
        return true;
    }, [amount, description, dispatch, userDetails?._id]);

    useEffect(() => {
        registerGuard?.({
            isDirty,
            canSave: isDirty && !!amount && !!description.trim() && !loading,
            title: "Закрыть корректировку баланса?",
            text: "В форме остались несохранённые данные корректировки.",
            details: isDirty ? [
                { label: "Сумма", before: "—", after: amount || "—" },
                { label: "Причина", before: "—", after: description || "—" },
            ] : [],
            onSave: handleModify,
            onDiscard: reset,
        });

        return () => registerGuard?.(null);
    }, [amount, description, handleModify, isDirty, loading, registerGuard, reset]);

    if (!userDetails) return null;

    return (
        <div className="userSection">
            <div className="userSection__head">
                <div>
                    <span>Финансы</span>
                    <h3>Личный баланс</h3>
                    <p>Личной подписки больше нет. Здесь остаётся личный баланс и ручная корректировка с обязательной причиной.</p>
                </div>
                {isDirty ? <b className="userSection__dirty">Есть изменения</b> : null}
            </div>

            <div className="userBillingBlock">
                <div className="userBillingBlock__summary">
                    <span>Текущий баланс</span>
                    <b>{Number(userDetails.balance || 0).toLocaleString("ru-RU")} ₽</b>
                    <small>Личная подписка отключена; подписка и рабочие лимиты относятся к офису.</small>
                </div>

                <div className="userBillingBlock__grid">
                    <label>
                        <span>Сумма</span>
                        <input
                            type="number"
                            value={amount}
                            placeholder="Например: 500 или -500"
                            onChange={(event) => {
                                setAmount(event.target.value);
                                setError("");
                                setMessage("");
                            }}
                        />
                    </label>
                    <label>
                        <span>Причина</span>
                        <input
                            type="text"
                            value={description}
                            placeholder="Причина корректировки"
                            onChange={(event) => {
                                setDescription(event.target.value);
                                setError("");
                                setMessage("");
                            }}
                        />
                    </label>
                </div>

                {message ? <div className="userManageForm__message">{message}</div> : null}
                {error ? <div className="userManageForm__error">{error}</div> : null}

                <div className="userManageForm__footer">
                    <button type="button" className="is-secondary" onClick={reset} disabled={loading || !isDirty}>
                        Очистить
                    </button>
                    <button type="button" onClick={handleModify} disabled={loading || !amount || !description.trim()}>
                        {loading ? "Обработка..." : "Изменить баланс"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserBillingBlock;
